import { NotesPanel } from "@/components/notes/NotesPanel";
import { useEffect, useState, useCallback } from "react";
import { Loader2, Pencil, Bus, Car, ArrowLeft } from "lucide-react";
import { StudentPhotoPicker } from "@/components/StudentPhotoPicker";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ChatThread from "@/components/ChatThread";
import { StudentOverview, StudentActivity, StudentAttendance, StudentForms } from "./StudentProfileTabs";
import { EventChip, tabCls } from "./ProfileBits";

const panelCls = "mt-4 data-[state=inactive]:hidden";

export function StudentProfileView({ studentId, onEdit, onBack, backLabel = "Back", refreshKey = 0 }) {
  const { user } = useAuth();
  const canEditPhoto = ["admin", "corporate_admin"].includes(user?.role);
  const [p, setP] = useState(null);
  const [thread, setThread] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/students/${studentId}/profile`);
      setP(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load profile");
      onBack?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, onBack, refreshKey]);

  const loadThread = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/threads/${studentId}`);
      setThread(data);
    } catch { /* not fatal */ }
  }, [studentId]);

  useEffect(() => { load(); loadThread(); }, [load, loadThread]);

  const reply = async (text, attachment) => {
    try {
      await api.post("/messages/send", { scope: "student", student_id: studentId, body: text, attachment: attachment || undefined });
      await loadThread();
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
      return false;
    }
  };

  const s = p?.student;
  return (
    <div className="space-y-4" data-testid="student-profile-page">
      {onBack && (
        <button onClick={onBack} data-testid="student-profile-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition">
          <ArrowLeft className="h-4 w-4" /> {backLabel}
        </button>
      )}
      <header className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-wrap items-start gap-4">
        <StudentPhotoPicker value={s?.photo_data_url || ""} size="h-20 w-20" disabled={!canEditPhoto || s?.active === false} testid="profile-photo" onChange={async (v) => {
          try { await api.put(`/students/${studentId}/photo`, { photo_data_url: v }); toast.success(v ? "Photo updated" : "Photo removed"); load(); }
          catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save photo"); }
        }} />
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 truncate" data-testid="student-profile-title">{s?.full_name || "Loading…"}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-slate-500">
            {p && <>
              {s.student_number && <span className="font-mono rounded-md bg-slate-100 px-2 py-0.5 text-slate-700" data-testid="student-profile-number">ID {s.student_number}</span>}
              <span>{p.class_name || s.grade || "No course"}</span>
              {s.rider_type === "bus"
                ? <span className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-0.5 font-bold text-blue-800"><Bus className="h-3 w-3" /> {p.bus_number ? `BUS ${p.bus_number}` : "No bus assigned"}</span>
                : s.car_number && <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 font-bold text-amber-800"><Car className="h-3 w-3" /> {s.car_number}</span>}
              {p.live_status && <EventChip type={p.live_status} />}
              {p.read_only && <span className="rounded-md bg-slate-100 px-2 py-0.5 text-slate-600">Read-only</span>}
              {s.active === false && <span className="rounded-md bg-amber-100 px-2 py-0.5 font-semibold text-amber-800" data-testid="student-inactive-chip">Deactivated · read-only</span>}
            </>}
          </div>
        </div>
        {p && !p.read_only && s.active !== false && onEdit && (
          <button onClick={() => onEdit(s)} data-testid="student-profile-edit-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition">
            <Pencil className="h-4 w-4" /> Edit
          </button>
        )}
      </header>

      {!p ? (
        <div className="py-16 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <Tabs defaultValue="overview">
          <TabsList className="bg-slate-100 rounded-lg p-1 h-auto flex-wrap justify-start">
            <TabsTrigger value="overview" className={tabCls} data-testid="student-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="activity" className={tabCls} data-testid="student-tab-activity">Check-ins ({p.events.length})</TabsTrigger>
            <TabsTrigger value="attendance" className={tabCls} data-testid="student-tab-attendance">Attendance</TabsTrigger>
            <TabsTrigger value="messages" className={tabCls} data-testid="student-tab-messages">Messages ({thread?.messages?.length ?? 0})</TabsTrigger>
            <TabsTrigger value="forms" className={tabCls} data-testid="student-tab-forms">Forms ({p.forms.length})</TabsTrigger>
            <TabsTrigger value="notes" className={tabCls} data-testid="student-tab-notes">Notes</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className={panelCls}><StudentOverview p={p} /></TabsContent>
          <TabsContent value="activity" className={panelCls}><StudentActivity p={p} /></TabsContent>
          <TabsContent value="attendance" className={panelCls}><StudentAttendance a={p.attendance} /></TabsContent>
          <TabsContent value="messages" className={panelCls}>
            <div className="rounded-xl border border-slate-200 overflow-hidden h-[60vh] flex flex-col bg-white" data-testid="student-profile-messages">
              <div className="px-4 py-2 border-b border-slate-100 text-[11px] text-slate-500">
                Parent contacts: {thread?.student?.contacts?.map((c) => c.name).filter(Boolean).join(", ") || "none on file"}
              </div>
              <div className="flex-1 min-h-0">
                {thread ? <ChatThread messages={thread.messages} onSend={reply} mySenderType="staff" emptyText="No messages with this family yet. Say hello below." />
                  : <div className="p-8 text-center text-sm text-slate-400">Loading conversation…</div>}
              </div>
            </div>
          </TabsContent>
          <TabsContent value="forms" className={panelCls}><StudentForms forms={p.forms} readOnly={p.read_only} onChanged={load} /></TabsContent>
          <TabsContent value="notes" className={panelCls}>
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="student-notes"><NotesPanel subject={{ type: "student", id: studentId }} /></div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
